import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import Button from "../../../../components/buttons/Button";
import TextField from "../../../../components/input/textfield/TextField";
import TextArea from "../../../../components/input/textarea/TextArea";
import Select from "../../../../components/input/select/Select";

import { useCreatorAlbums } from "../hooks/useAlbums";

import "./CreatorEditAlbumForm.css";

const VISIBILITY_OPTIONS = [
  { label: "Public", value: "public" },
  { label: "Unlisted", value: "unlisted" },
  { label: "Private", value: "private" },
];

export default function CreatorEditAlbumForm({
  username,
  album,
  onClose,
  onRefresh,
}) {
  const { loading, error, getCreatorAlbumEditData, updateCreatorAlbum } =
    useCreatorAlbums(username);

  const albumId = Number(album?.albumId);

  const [formData, setFormData] = useState({
    title: album?.title || "",
    caption: album?.caption || "",
    visibility: album?.visibility || "private",
  });

  const [initializing, setInitializing] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const isBusy = loading || initializing || saving;

  const canSubmit =
    Boolean(formData.title.trim()) &&
    VISIBILITY_OPTIONS.some((option) => option.value === formData.visibility) &&
    !isBusy;

  useEffect(() => {
    let active = true;

    const loadAlbum = async () => {
      setInitializing(true);
      setFormError("");

      if (!username || !Number.isSafeInteger(albumId) || albumId <= 0) {
        setFormError("Invalid album information.");
        setInitializing(false);
        return;
      }

      try {
        const result = await getCreatorAlbumEditData(albumId);

        if (!active) return;

        if (result === false || result?.success === false) {
          setFormError("Unable to load album details.");
          return;
        }

        const data = result?.album || result || album;

        const visibility = String(data?.visibility || "private").toLowerCase();

        setFormData({
          title: data?.title || "",
          caption: data?.caption || "",
          visibility: VISIBILITY_OPTIONS.some(
            (option) => option.value === visibility,
          )
            ? visibility
            : "private",
        });
      } catch (err) {
        if (active) {
          setFormError(
            err?.response?.data?.message || "Unable to load album details.",
          );
        }
      } finally {
        if (active) {
          setInitializing(false);
        }
      }
    };

    void loadAlbum();

    return () => {
      active = false;
    };
  }, [username, albumId]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        event.preventDefault();
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [saving, onClose]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    if (!username || !Number.isSafeInteger(albumId) || albumId <= 0) {
      setFormError("Album information is missing.");
      return;
    }

    setSaving(true);
    setFormError("");

    try {
      const result = await updateCreatorAlbum({
        albumId,
        title: formData.title.trim(),
        caption: formData.caption.trim(),
        visibility: formData.visibility,
      });

      if (result === false || result?.success === false) {
        setFormError(result?.message || "Unable to save album changes.");
        return;
      }

      await onRefresh?.();
      onClose?.();
    } catch (err) {
      console.error("Unable to update album:", err);

      setFormError(
        err?.response?.data?.message || "Unable to save album changes.",
      );
    } finally {
      setSaving(false);
    }
  };

  const errorMessage =
    formError || (typeof error === "string" ? error : error?.message || "");

  return createPortal(
    <div
      className="creator-edit-album"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <section
        className="creator-edit-album__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="creator-edit-album-title"
      >
        <header className="creator-edit-album__header">
          <div className="creator-edit-album__header-info">
            <h2 id="creator-edit-album-title">Edit Album</h2>
            <p>Update your album details and visibility.</p>
          </div>

          <button
            type="button"
            className="creator-edit-album__close"
            onClick={handleClose}
            disabled={saving}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </header>

        <form className="creator-edit-album__form" onSubmit={handleSubmit}>
          <div className="creator-edit-album__content">
            {errorMessage && (
              <p className="creator-edit-album__error" role="alert">
                {errorMessage}
              </p>
            )}

            <TextField
              name="title"
              label="Title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Album title"
              maxLength={60}
              required
              disabled={isBusy}
            />

            <TextArea
              name="caption"
              label="Caption"
              value={formData.caption}
              onChange={handleChange}
              placeholder="Write a caption for your album"
              maxLength={2000}
              maxHeight={180}
              disabled={isBusy}
            />

            <Select
              name="visibility"
              label="Visibility"
              value={formData.visibility}
              onChange={handleChange}
              items={VISIBILITY_OPTIONS}
              required
              disabled={isBusy}
            />
          </div>

          <footer className="creator-edit-album__footer">
            <span className="creator-edit-album__status">
              {initializing
                ? "Loading album..."
                : saving
                  ? "Saving changes..."
                  : ""}
            </span>

            <div className="creator-edit-album__actions">
              <Button
                type="button"
                value="Cancel"
                variant="primary-outlined"
                onClick={handleClose}
                disabled={saving}
              />

              <Button
                type="submit"
                value={saving ? "Saving..." : "Save changes"}
                variant="primary"
                disabled={!canSubmit}
              />
            </div>
          </footer>
        </form>
      </section>
    </div>,
    document.body,
  );
}
